import { useEffect } from 'react'

// Both are rendered by @fs/zion-root, above everything this app mounts.
const SKIP_LINK_SELECTOR = 'a[href="#main"]'
const MAIN_SELECTOR = '#main'

// Idempotent, so it is safe to call on mount and again before each redirect. An
// existing value is left alone in case zion-root ever sets one of its own.
const ensureMainIsFocusable = () => {
  const main = document.querySelector(MAIN_SELECTOR)
  if (main && !main.hasAttribute('tabindex')) {
    main.setAttribute('tabindex', '-1')
  }
}

const focusIsLost = () => {
  const active = document.activeElement
  return !active || active === document.body || !document.contains(active)
}

/**
 * Keeps "Skip to Main Content" reachable with one Tab after a client-side route
 * change, and makes activating it actually move focus (DSC-10126).
 *
 * Two things are wrong, and each needs its half or the other half misbehaves.
 *
 * 1. `#main` carries no tabindex, so it cannot take focus. Activating the skip link
 *    therefore moves focus nowhere: it only sets the browser's *sequential focus
 *    navigation starting point* - the invisible position Tab resumes from, separate
 *    from `document.activeElement` - and leaves focus on `<body>`. Chrome's fallback
 *    makes the next Tab land inside main anyway, which is why this looked fine.
 *    `tabindex="-1"` is the standard fix for a skip-link target and makes the move
 *    real, so focus is somewhere afterwards rather than nowhere.
 *
 * 2. On a route change the link you activated unmounts with the old view.
 *    `activeElement` falls to `<body>` synchronously, but the starting point stays at
 *    the DOM position the removed element occupied, so the next Tab resumes from the
 *    middle of the new page. Measured on /myname/meaning/: the first Tab after the
 *    route change landed on tab stop 23 of 25, near the foot of the page, leaving the
 *    skip link at stop 1 reachable only by tabbing to the end and wrapping - the
 *    ticket's "10+ presses". Redirecting Tab to the skip link when focus has been lost
 *    fixes that.
 *
 * Why they are inseparable: without (1), activating the skip link leaves focus lost,
 * so (2) fires on the very next Tab and sends the visitor back to the skip link they
 * just used - a loop between the link and itself, and worse than the original bug.
 * With (1) focus lands on `#main`, which is in the document, so (2) stays inert and
 * Tab continues past the header as it should.
 *
 * Two route-change approaches were tried for (2) before settling on the key handler,
 * and both failed in the browser. `document.body.focus()` is a no-op, because body is
 * already the active element by then - only an explicit focus() moves the starting
 * point. Focusing the element before the skip link does move it, but not durably from
 * an effect keyed on the path: these routes mount asynchronously, so the effect fires
 * before the view settles and later renders undo it. Reading focus at the moment Tab
 * is pressed has no such race.
 */
export default function useRestoreTabOrderOnRouteChange() {
  useEffect(() => {
    // zion-root keeps one #main for the life of the app, so setting this once on mount
    // is enough. It is deliberately not keyed on the route: reading useLocation here
    // would put a router dependency into DsApp, whose own tests mock @fs/zion-router
    // without it. The handler below re-asserts it anyway, which covers the element
    // being replaced at any point before someone actually tabs.
    ensureMainIsFocusable()

    const onKeyDown = (event) => {
      // Shift+Tab from nowhere should still reach the end of the document, which is
      // what the browser already does - only forward Tab is redirected.
      if (event.key !== 'Tab' || event.shiftKey || event.defaultPrevented) return
      if (!focusIsLost()) return

      // Owned by zion-root, so optional: a shell with no skip link has no tab order
      // to restore, and leaving the browser to it is right.
      const skipLink = document.querySelector(SKIP_LINK_SELECTOR)
      if (!skipLink) return

      // Before handing them the skip link, make sure activating it will land focus on
      // something - otherwise Enter leaves focus lost and the next Tab comes straight
      // back here, looping between the link and itself.
      ensureMainIsFocusable()

      event.preventDefault()
      skipLink.focus()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])
}
